/**
 * useCustomProjections Hook
 *
 * Calculates adjusted projections based on user customizations.
 * - NBA/NHL: minutes × per-minute rates.
 * - NFL: volume × per-unit rates, where volume is pass attempts (passing), rush attempts
 *   (rushing) and targets (receiving) — the football stand-ins for minutes.
 * Returns baseline rates/volumes, current projections, and percentage changes.
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../store/reducer';
import { PlayerData } from '../data/types';
import { PlayerCustomization, PlayerRates, PlayerVolumes } from '../store/types';

export interface CustomProjectionValues {
  // NBA stats
  pts: number;
  reb: number;
  ast: number;
  threes: number;
  pra: number;
  // NHL stats
  goals: number;
  nhlAssists: number;
  sog: number;
  saves: number;
  nhlPoints: number;
  blocks: number;
  // NFL volumes (also markets in their own right)
  passAtt: number;
  rushAtt: number;
  targets: number;
  receptions: number;
  // NFL derived stats
  passYds: number;
  passTds: number;
  comp: number;
  ints: number;
  rushYds: number;
  rushTds: number;
  recYds: number;
  recTds: number;
  passRushYds: number;
  rushRecYds: number;
  anyTd: number;
  rushRecTds: number;
}

export interface CustomProjectionChanges extends CustomProjectionValues {
  minutes: number;
}

export interface CustomProjections {
  // Current values (customized or baseline)
  minutes: number;
  rates: PlayerRates;
  volumes: PlayerVolumes;
  projections: CustomProjectionValues;
  // Baseline values from original data
  baseline: {
    minutes: number;
    rates: PlayerRates;
    volumes: PlayerVolumes;
    projections: CustomProjectionValues;
  };
  // Percentage change from baseline
  changes: CustomProjectionChanges;
  // Whether player has any customizations
  hasCustomizations: boolean;
}

export const ZERO_VOLUMES: PlayerVolumes = { passAtt: 0, rushAtt: 0, targets: 0 };

const ZERO_RATES: PlayerRates = {
  pts: 0, reb: 0, ast: 0, threes: 0,
  goals: 0, nhlAssists: 0, sog: 0, saves: 0, nhlPoints: 0, blocks: 0,
  passYdsPerAtt: 0, compPerAtt: 0, passTdPerAtt: 0, intPerAtt: 0,
  rushYdsPerAtt: 0, rushTdPerAtt: 0, recYdsPerRec: 0, recTdPerRec: 0,
};

// NHL/NFL baseline lookup from props (these leagues have no dedicated projection fields).
// `exact` matches the normalized stat code exactly (NFL codes like "Rec" would otherwise
// substring-match "Rec Yds"); the default fuzzy mode preserves the original NHL behaviour.
/**
 * A stat's projection for this player. The projection file (carried on the outcome, independent of markets) is
 * the source of truth; the player's markets are only a fallback for feeds that predate it. Reading from markets
 * meant a player priced for one stat showed zeros for every other, and rates computed from those zeros.
 */
const getBaselineFromProps = (player: PlayerData, statNames: string[], exact = false): number => {
  const set = player.projectionSet;
  if (set) {
    for (const name of statNames) {
      const hit = set[name] ?? set[Object.keys(set).find((k) => k.toLowerCase() === name.toLowerCase()) ?? ''];
      if (typeof hit === 'number') return hit;
    }
  }

  for (const prop of player.props || []) {
    const propStatLower = prop.stat.toLowerCase();
    const propOriginalStatLower = (prop.originalStat || '').toLowerCase();
    const matches = statNames.some((s) => {
      const searchLower = s.toLowerCase();
      if (exact) return propStatLower === searchLower;
      return propStatLower.includes(searchLower) ||
             propOriginalStatLower.includes(searchLower) ||
             propStatLower === searchLower;
    });
    if (matches) {
      if (prop.projection) return prop.projection;
      if (prop.sportsbooks?.[0]?.line) return prop.sportsbooks[0].line;
    }
  }
  return 0;
};

const safeDiv = (num: number, den: number) => (den > 0 ? num / den : 0);

const calculateChange = (current: number, baseline: number): number => {
  if (baseline === 0) return 0;
  return ((current - baseline) / baseline) * 100;
};

/** NFL baseline volumes straight from the feed's markets. */
/**
 * Volume knobs come from the projection file, carried on the outcome like NBA's minutes — not from betting
 * markets. Only one book prices targets at all, so sourcing them from props would leave most receivers with
 * no lever. Props are kept as a fallback for feeds that predate those fields.
 */
export function getBaselineVolumes(player: PlayerData): PlayerVolumes {
  return {
    passAtt: getBaselineFromProps(player, ['Pass Att'], true),
    rushAtt: getBaselineFromProps(player, ['Rush Att'], true),
    // Falls back to receptions when nothing supplies targets (older feed, or a player the projection file
    // has no targets for): the catch rate then works out to 1 and the model degrades to the old one.
    targets: getBaselineFromProps(player, ['Targets'], true) || getBaselineFromProps(player, ['Rec'], true),
  };
}

/**
 * Helper function to get baseline rates from a player
 * Useful when initializing a customization
 */
export function getBaselineRates(player: PlayerData): PlayerRates {
  const minutes = parseFloat(player.minutes) || 0;

  // NFL per-unit rates (independent of minutes)
  const volumes = getBaselineVolumes(player);
  const nfl = {
    passYdsPerAtt: safeDiv(getBaselineFromProps(player, ['Pass Yds'], true), volumes.passAtt),
    compPerAtt: safeDiv(getBaselineFromProps(player, ['Comp'], true), volumes.passAtt),
    passTdPerAtt: safeDiv(getBaselineFromProps(player, ['Pass TDs'], true), volumes.passAtt),
    intPerAtt: safeDiv(getBaselineFromProps(player, ['INT'], true), volumes.passAtt),
    rushYdsPerAtt: safeDiv(getBaselineFromProps(player, ['Rush Yds'], true), volumes.rushAtt),
    rushTdPerAtt: safeDiv(getBaselineFromProps(player, ['Rush TDs'], true), volumes.rushAtt),
    // Receptions derive from targets via the catch rate, mirroring completions from pass attempts
    catchPerTarget: safeDiv(getBaselineFromProps(player, ['Rec'], true), volumes.targets),
    recYdsPerRec: safeDiv(getBaselineFromProps(player, ['Rec Yds'], true), getBaselineFromProps(player, ['Rec'], true)),
    recTdPerRec: safeDiv(getBaselineFromProps(player, ['Rec TDs'], true), getBaselineFromProps(player, ['Rec'], true)),
  };

  if (minutes === 0) {
    return { ...ZERO_RATES, ...nfl };
  }

  // NBA stats from projections
  const pts = (parseFloat(player.projections.pts) || 0) / minutes;
  const reb = (parseFloat(player.projections.reb) || 0) / minutes;
  const ast = (parseFloat(player.projections.ast) || 0) / minutes;
  const threes = (parseFloat(player.projections.threes) || 0) / minutes;

  // NHL stats derived from props
  const goals = getBaselineFromProps(player, ['goals', 'total player goals']) / minutes;
  const nhlAssists = getBaselineFromProps(player, ['total assists']) / minutes;
  const sog = getBaselineFromProps(player, ['shots on goal', 'total shots']) / minutes;
  const saves = getBaselineFromProps(player, ['saves', 'goalie saves']) / minutes;
  const nhlPoints = getBaselineFromProps(player, ['total points']) / minutes;
  const blocks = getBaselineFromProps(player, ['blocked shots', 'blocks']) / minutes;

  return { pts, reb, ast, threes, goals, nhlAssists, sog, saves, nhlPoints, blocks, ...nfl };
}

/**
 * Pure projection math shared by the hook and tests.
 * `customization` is assumed to already be validated against the game.
 */
export function computeCustomProjections(
  player: PlayerData,
  customization: PlayerCustomization | undefined
): CustomProjections {
  const baselineMinutes = parseFloat(player.minutes) || 0;

  // NBA baseline stats
  const baselinePts = parseFloat(player.projections.pts) || 0;
  const baselineReb = parseFloat(player.projections.reb) || 0;
  const baselineAst = parseFloat(player.projections.ast) || 0;
  const baselineThrees = parseFloat(player.projections.threes) || 0;
  const baselinePra = parseFloat(player.projections.pra) || 0;

  // NHL baseline stats - derive from props (NHL has no dedicated projections)
  const baselineGoals = getBaselineFromProps(player, ['goals', 'total player goals']);
  const baselineNhlAssists = getBaselineFromProps(player, ['total assists']);
  const baselineSog = getBaselineFromProps(player, ['shots on goal', 'total shots']);
  const baselineSaves = getBaselineFromProps(player, ['saves', 'goalie saves']);
  const baselineNhlPoints = getBaselineFromProps(player, ['total points']);
  const baselineBlocks = getBaselineFromProps(player, ['blocked shots', 'blocks']);

  // NFL baselines from props (exact stat codes)
  const baselineVolumes = getBaselineVolumes(player);
  const nflBase = {
    passYds: getBaselineFromProps(player, ['Pass Yds'], true),
    passTds: getBaselineFromProps(player, ['Pass TDs'], true),
    comp: getBaselineFromProps(player, ['Comp'], true),
    ints: getBaselineFromProps(player, ['INT'], true),
    rushYds: getBaselineFromProps(player, ['Rush Yds'], true),
    rushTds: getBaselineFromProps(player, ['Rush TDs'], true),
    receptions: getBaselineFromProps(player, ['Rec'], true),
    recYds: getBaselineFromProps(player, ['Rec Yds'], true),
    recTds: getBaselineFromProps(player, ['Rec TDs'], true),
    passRushYds: getBaselineFromProps(player, ['Pass+Rush Yds'], true),
    rushRecYds: getBaselineFromProps(player, ['Rush+Rec Yds'], true),
    anyTd: getBaselineFromProps(player, ['Any TD'], true),
    rushRecTds: getBaselineFromProps(player, ['Rush+Rec TDs'], true),
  };

  const baselineRates = getBaselineRates(player);

  const baseline = {
    minutes: baselineMinutes,
    rates: baselineRates,
    volumes: baselineVolumes,
    projections: {
      pts: baselinePts, reb: baselineReb, ast: baselineAst, threes: baselineThrees, pra: baselinePra,
      goals: baselineGoals, nhlAssists: baselineNhlAssists, sog: baselineSog, saves: baselineSaves,
      nhlPoints: baselineNhlPoints, blocks: baselineBlocks,
      passAtt: baselineVolumes.passAtt, rushAtt: baselineVolumes.rushAtt, targets: baselineVolumes.targets,
      ...nflBase,
    },
  };

  // Apply customizations if they exist, otherwise use baseline
  const currentMinutes = customization?.minutes ?? baselineMinutes;
  const currentRates: PlayerRates = customization?.rates ?? baselineRates;
  const currentVolumes: PlayerVolumes = customization?.volumes ?? baselineVolumes;

  const r = (key: keyof PlayerRates) => currentRates[key] ?? 0;

  // NFL derived stats (volume × rate). Combos carry the baseline's residual so a market
  // whose components aren't all in the feed still moves with the ones that are.
  const passYds = currentVolumes.passAtt * r('passYdsPerAtt');
  const passTds = currentVolumes.passAtt * r('passTdPerAtt');
  const comp = currentVolumes.passAtt * r('compPerAtt');
  const ints = currentVolumes.passAtt * r('intPerAtt');
  const rushYds = currentVolumes.rushAtt * r('rushYdsPerAtt');
  const rushTds = currentVolumes.rushAtt * r('rushTdPerAtt');
  const receptions = currentVolumes.targets * r('catchPerTarget');
  const recYds = receptions * r('recYdsPerRec');
  const recTds = receptions * r('recTdPerRec');
  const combo = (base: number, parts: Array<[number, number]>) =>
    Math.max(0, base + parts.reduce((sum, [cur, b]) => sum + (cur - b), 0));

  // TD markets: when the feed carries Rush TD / Rec TD components, Any TD follows them.
  // Most players only have the Any TD market itself, so fall back to scaling it with touches
  // (carries + receptions), which is what a volume change does to touchdown equity.
  const hasTdComponents = nflBase.rushTds > 0 || nflBase.recTds > 0;
  const baseReceptions = baselineVolumes.targets * (baseline.rates.catchPerTarget ?? 0);
  const baseTouches = baselineVolumes.rushAtt + baseReceptions;
  const currentTouches = currentVolumes.rushAtt + receptions;
  const scaleByTouches = (base: number) => (baseTouches > 0 ? Math.max(0, base * (currentTouches / baseTouches)) : base);
  const anyTd = hasTdComponents
    ? combo(nflBase.anyTd, [[rushTds, nflBase.rushTds], [recTds, nflBase.recTds]])
    : scaleByTouches(nflBase.anyTd);
  const rushRecTds = hasTdComponents
    ? combo(nflBase.rushRecTds, [[rushTds, nflBase.rushTds], [recTds, nflBase.recTds]])
    : scaleByTouches(nflBase.rushRecTds);

  const currentProjections: CustomProjectionValues = {
    // NBA
    pts: currentMinutes * r('pts'),
    reb: currentMinutes * r('reb'),
    ast: currentMinutes * r('ast'),
    threes: currentMinutes * r('threes'),
    pra: currentMinutes * (r('pts') + r('reb') + r('ast')),
    // NHL
    goals: currentMinutes * r('goals'),
    nhlAssists: currentMinutes * r('nhlAssists'),
    sog: currentMinutes * r('sog'),
    saves: currentMinutes * r('saves'),
    nhlPoints: currentMinutes * r('nhlPoints'),
    blocks: currentMinutes * r('blocks'),
    // NFL
    passAtt: currentVolumes.passAtt,
    rushAtt: currentVolumes.rushAtt,
    targets: currentVolumes.targets,
    receptions,
    passYds, passTds, comp, ints, rushYds, rushTds, recYds, recTds,
    passRushYds: combo(nflBase.passRushYds, [[passYds, nflBase.passYds], [rushYds, nflBase.rushYds]]),
    rushRecYds: combo(nflBase.rushRecYds, [[rushYds, nflBase.rushYds], [recYds, nflBase.recYds]]),
    anyTd,
    rushRecTds,
  };

  const changes = { minutes: calculateChange(currentMinutes, baselineMinutes) } as CustomProjectionChanges;
  (Object.keys(currentProjections) as Array<keyof CustomProjectionValues>).forEach((key) => {
    changes[key] = calculateChange(currentProjections[key], baseline.projections[key]);
  });

  return {
    minutes: currentMinutes,
    rates: currentRates,
    volumes: currentVolumes,
    projections: currentProjections,
    baseline,
    changes,
    hasCustomizations: !!customization,
  };
}

export function useCustomProjections(player: PlayerData, gameId: string, eventStartTime: string): CustomProjections {
  const storedCustomization = useSelector(
    (state: IRootState) => state.stokasticProps.playerCustomizations[player.id]
  );

  return useMemo(() => {
    // Only apply customization if gameId AND eventStartTime match
    // (prevents stale edits from different games, including doubleheaders)
    const customization =
      storedCustomization?.gameId === gameId && storedCustomization?.eventStartTime === eventStartTime
        ? storedCustomization
        : undefined;
    return computeCustomProjections(player, customization);
  }, [player, gameId, eventStartTime, storedCustomization]);
}

/**
 * Create a full PlayerCustomization object from just minutes change
 * Preserves baseline rates when only minutes are being modified
 */
export function createMinutesCustomization(
  player: PlayerData,
  gameId: string,
  eventStartTime: string,
  newMinutes: number
): PlayerCustomization {
  return {
    gameId,
    eventStartTime,
    minutes: newMinutes,
    rates: getBaselineRates(player),
  };
}

export default useCustomProjections;
